import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Download, Loader2, Paperclip, Plus, Search, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  adminSupportInquiriesApi,
  type CenterInquiryCategory,
  type CenterInquiryDetail,
  type CenterInquiryListItem,
  type CenterInquiryStatus,
} from '@/api/adminSupportInquiries'
import { AdminPagination } from '@/components/admin/members/AdminPagination'

const CATEGORY_OPTIONS: CenterInquiryCategory[] = ['content', 'system', 'billing', 'policy', 'other']
const STATUS_OPTIONS: Array<CenterInquiryStatus | 'all'> = ['all', 'pending', 'answered']
const MAX_FILES = 3
const MAX_FILE_BYTES = 10 * 1024 * 1024
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const
const FILE_ACCEPT = ALLOWED_MIME_TYPES.join(',')
const ALLOWED_FILE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'mp4',
  'webm',
  'mov',
  'pdf',
  'doc',
  'docx',
])

type FileValidationKey = 'maxFiles' | 'maxFileSize' | 'fileType'

function formatDateTime(value: string | null, locale: string, emptyValue: string): string {
  if (!value) return emptyValue
  return new Date(value).toLocaleString(locale)
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

function getFileValidationError(files: File[]): FileValidationKey | null {
  if (files.length > MAX_FILES) return 'maxFiles'
  if (files.some((file) => file.size > MAX_FILE_BYTES)) return 'maxFileSize'
  if (files.some((file) => {
    const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() ?? '' : ''
    return (
      !ALLOWED_MIME_TYPES.includes(file.type as (typeof ALLOWED_MIME_TYPES)[number]) ||
      !ALLOWED_FILE_EXTENSIONS.has(extension)
    )
  })) {
    return 'fileType'
  }
  return null
}

function statusBadgeClass(status: CenterInquiryStatus): string {
  return status === 'answered'
    ? 'bg-green-100 text-green-700'
    : 'bg-amber-100 text-amber-700'
}

function attachmentLinkClass(downloadUrl: string | null): string {
  return downloadUrl
    ? 'text-primary-600 hover:text-primary-700'
    : 'text-gray-400'
}

function useInquiryLabels() {
  const { t } = useTranslation('admin')

  return useMemo(
    () => ({
      categories: {
        content: t('supportInquiries.category.content'),
        system: t('supportInquiries.category.system'),
        billing: t('supportInquiries.category.billing'),
        policy: t('supportInquiries.category.policy'),
        other: t('supportInquiries.category.other'),
      } satisfies Record<CenterInquiryCategory, string>,
      statuses: {
        pending: t('supportInquiries.status.pending'),
        answered: t('supportInquiries.status.answered'),
      } satisfies Record<CenterInquiryStatus, string>,
    }),
    [t],
  )
}

function useLocale() {
  const { i18n } = useTranslation('admin')
  return i18n.language === 'en' ? 'en-US' : 'ko-KR'
}

export default function Inquiries() {
  const { t } = useTranslation('admin')
  const labels = useInquiryLabels()
  const locale = useLocale()
  const [status, setStatus] = useState<CenterInquiryStatus | 'all'>('all')
  const [keywordInput, setKeywordInput] = useState('')
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(0)
  const [composeOpen, setComposeOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const listQuery = useQuery({
    queryKey: ['lc-admin', 'support-inquiries', { status, keyword, page }],
    queryFn: () =>
      adminSupportInquiriesApi.list({
        status: status === 'all' ? undefined : status,
        keyword: keyword || undefined,
        page,
        size: 20,
      }),
  })

  const items = listQuery.data?.content ?? []
  const totalPages = listQuery.data?.totalPages ?? 0
  const emptyValue = t('supportInquiries.common.emptyValue')

  const handleSearch = () => {
    setKeyword(keywordInput.trim())
    setPage(0)
  }

  const handleStatusChange = (next: CenterInquiryStatus | 'all') => {
    setStatus(next)
    setPage(0)
  }

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">{t('supportInquiries.title')}</h1>
          <p className="text-sm text-gray-500">{t('supportInquiries.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setComposeOpen(true)}
          className="inline-flex h-10 items-center gap-2 rounded-md bg-primary-600 px-4 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          {t('supportInquiries.actions.create')}
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_OPTIONS.map((option) => {
              const active = status === option
              const label =
                option === 'all'
                  ? t('supportInquiries.filters.all')
                  : labels.statuses[option]

              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleStatusChange(option)}
                  className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
                    active
                      ? 'border-primary-200 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                  }`}
                >
                  {label}
                </button>
              )
            })}
          </div>

          <div className="flex items-center gap-2">
            <label className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                aria-label={t('supportInquiries.search.ariaLabel')}
                value={keywordInput}
                onChange={(event) => setKeywordInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSearch()
                }}
                placeholder={t('supportInquiries.search.placeholder')}
                className="h-10 w-72 rounded-md border border-gray-300 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none"
              />
            </label>
            <button
              type="button"
              onClick={handleSearch}
              className="h-10 rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('supportInquiries.actions.search')}
            </button>
          </div>
        </div>

        {listQuery.isLoading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-16 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('supportInquiries.list.loading')}
          </div>
        ) : listQuery.isError ? (
          <div className="space-y-3 px-4 py-16 text-center">
            <p className="text-sm text-gray-500">{t('supportInquiries.list.loadError')}</p>
            <button
              type="button"
              onClick={() => listQuery.refetch()}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('supportInquiries.actions.retry')}
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500">
                  <tr>
                    <th className="px-4 py-3">{t('supportInquiries.table.category')}</th>
                    <th className="px-4 py-3">{t('supportInquiries.table.title')}</th>
                    <th className="px-4 py-3">{t('supportInquiries.table.createdAt')}</th>
                    <th className="px-4 py-3">{t('supportInquiries.table.answeredAt')}</th>
                    <th className="px-4 py-3">{t('supportInquiries.table.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {items.map((item) => (
                    <InquiryRow
                      key={item.inquiryId}
                      item={item}
                      locale={locale}
                      emptyValue={emptyValue}
                      categoryLabels={labels.categories}
                      statusLabels={labels.statuses}
                      onOpen={() => setSelectedId(item.inquiryId)}
                    />
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-16 text-center text-sm text-gray-400">
                        {t('supportInquiries.list.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <AdminPagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>

      {composeOpen && <ComposeModal onClose={() => setComposeOpen(false)} />}
      {selectedId !== null && (
        <DetailModal inquiryId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  )
}

function InquiryRow({
  item,
  locale,
  emptyValue,
  categoryLabels,
  statusLabels,
  onOpen,
}: {
  item: CenterInquiryListItem
  locale: string
  emptyValue: string
  categoryLabels: Record<CenterInquiryCategory, string>
  statusLabels: Record<CenterInquiryStatus, string>
  onOpen: () => void
}) {
  return (
    <tr className="cursor-pointer text-gray-800 hover:bg-gray-50" onClick={onOpen}>
      <td className="px-4 py-3">{categoryLabels[item.category]}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{item.title}</span>
          {item.hasAttachments && <Paperclip className="h-4 w-4 text-gray-400" aria-hidden />}
        </div>
      </td>
      <td className="px-4 py-3 text-gray-600">{formatDateTime(item.createdAt, locale, emptyValue)}</td>
      <td className="px-4 py-3 text-gray-600">{formatDateTime(item.answeredAt, locale, emptyValue)}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(item.status)}`}>
          {statusLabels[item.status]}
        </span>
      </td>
    </tr>
  )
}

function ComposeModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('admin')
  const labels = useInquiryLabels()
  const queryClient = useQueryClient()
  const [category, setCategory] = useState<CenterInquiryCategory | ''>('')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [fileValidationMessage, setFileValidationMessage] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: (payload: {
      category: CenterInquiryCategory
      title: string
      content: string
      files: File[]
    }) => adminSupportInquiriesApi.create(payload),
    onSuccess: async () => {
      toast.success(t('supportInquiries.toast.createSuccess'))
      await queryClient.invalidateQueries({ queryKey: ['lc-admin', 'support-inquiries'] })
      onClose()
    },
    onError: () => {
      toast.error(t('supportInquiries.toast.createFailed'))
    },
  })

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files ?? [])
    const validationError = getFileValidationError(nextFiles)

    if (validationError) {
      const message = t(`supportInquiries.validation.${validationError}`)
      setFileValidationMessage(message)
      toast.warning(message)
      setFiles([])
      event.target.value = ''
      return
    }

    setFileValidationMessage(null)
    setFiles(nextFiles)
    event.target.value = ''
  }

  const handleRemoveFile = (index: number) => {
    setFileValidationMessage(null)
    setFiles((current) => current.filter((_, currentIndex) => currentIndex !== index))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!category) {
      toast.warning(t('supportInquiries.validation.categoryRequired'))
      return
    }
    if (title.trim().length === 0 || title.length > 100) {
      toast.warning(t('supportInquiries.validation.titleLength'))
      return
    }
    if (content.trim().length === 0 || content.length > 2000) {
      toast.warning(t('supportInquiries.validation.contentLength'))
      return
    }
    const validationError = getFileValidationError(files)
    if (validationError) {
      toast.warning(t(`supportInquiries.validation.${validationError}`))
      return
    }

    createMutation.mutate({
      category,
      title: title.trim(),
      content: content.trim(),
      files,
    })
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="compose-inquiry-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4">
          <h2 id="compose-inquiry-title" className="text-lg font-semibold text-gray-900">
            {t('supportInquiries.compose.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('supportInquiries.actions.close')}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              {t('supportInquiries.compose.category')}
            </span>
            <select
              aria-label={t('supportInquiries.compose.category')}
              value={category}
              onChange={(event) => setCategory(event.target.value as CenterInquiryCategory | '')}
              className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none"
            >
              <option value="">{t('supportInquiries.compose.categoryPlaceholder')}</option>
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {labels.categories[option]}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              {t('supportInquiries.compose.subject')}
            </span>
            <input
              aria-label={t('supportInquiries.compose.subject')}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={100}
              className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none"
            />
            <span className="mt-1 block text-right text-xs text-gray-400">{title.length}/100</span>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              {t('supportInquiries.compose.content')}
            </span>
            <textarea
              aria-label={t('supportInquiries.compose.content')}
              value={content}
              onChange={(event) => setContent(event.target.value)}
              maxLength={2000}
              rows={8}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 whitespace-pre-wrap focus:border-primary-500 focus:outline-none"
            />
            <span className="mt-1 block text-right text-xs text-gray-400">{content.length}/2000</span>
          </label>

          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">
              {t('supportInquiries.compose.attachments')}
            </span>
            <label className="flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-600 hover:border-gray-400 hover:bg-gray-100">
              <Paperclip className="h-4 w-4" />
              {t('supportInquiries.compose.fileButton')}
              <input
                aria-label={t('supportInquiries.compose.fileButton')}
                type="file"
                multiple
                accept={FILE_ACCEPT}
                className="sr-only"
                onChange={handleFilesChange}
              />
            </label>
            <p className="mt-1 text-xs text-gray-400">{t('supportInquiries.compose.fileHint')}</p>
            {fileValidationMessage && (
              <p role="alert" className="mt-2 text-sm text-red-600">
                {fileValidationMessage}
              </p>
            )}
            {files.length > 0 && (
              <ul className="mt-3 space-y-2">
                {files.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      aria-label={t('supportInquiries.compose.removeFile', { fileName: file.name })}
                      className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 rounded-md border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('supportInquiries.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="inline-flex h-10 items-center gap-2 rounded-md bg-primary-600 px-4 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('supportInquiries.actions.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function DetailModal({ inquiryId, onClose }: { inquiryId: number; onClose: () => void }) {
  const { t } = useTranslation('admin')
  const detailQuery = useQuery({
    queryKey: ['lc-admin', 'support-inquiries', inquiryId],
    queryFn: () => adminSupportInquiriesApi.detail(inquiryId),
  })

  const detail = detailQuery.data
  const latestReply = useMemo(() => {
    return detail?.replies.filter((reply) => reply.authorRole === 'super_admin').at(-1) ?? null
  }, [detail])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="inquiry-detail-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-lg bg-white p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4">
          <h2 id="inquiry-detail-title" className="text-lg font-semibold text-gray-900">
            {t('supportInquiries.detail.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('supportInquiries.actions.close')}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {detailQuery.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('supportInquiries.detail.loading')}
          </div>
        ) : detailQuery.isError || !detail ? (
          <div className="space-y-3 py-16 text-center">
            <p className="text-sm text-gray-500">{t('supportInquiries.detail.loadError')}</p>
            <button
              type="button"
              onClick={() => detailQuery.refetch()}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('supportInquiries.actions.retry')}
            </button>
          </div>
        ) : (
          <DetailBody detail={detail} latestReply={latestReply} onClose={onClose} />
        )}
      </div>
    </div>
  )
}

function DetailBody({
  detail,
  latestReply,
  onClose,
}: {
  detail: CenterInquiryDetail
  latestReply: CenterInquiryDetail['replies'][number] | null
  onClose: () => void
}) {
  const { t } = useTranslation('admin')
  const labels = useInquiryLabels()
  const locale = useLocale()
  const emptyValue = t('supportInquiries.common.emptyValue')

  return (
    <div className="mt-5 space-y-6">
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-600">
            {labels.categories[detail.category]}
          </span>
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(detail.status)}`}>
            {labels.statuses[detail.status]}
          </span>
        </div>
        <h3 className="text-base font-semibold text-gray-900">{detail.title}</h3>
        <dl className="grid gap-2 text-sm text-gray-600 md:grid-cols-2">
          <div>
            <dt className="text-xs text-gray-400">{t('supportInquiries.table.createdAt')}</dt>
            <dd>{formatDateTime(detail.createdAt, locale, emptyValue)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-400">{t('supportInquiries.table.answeredAt')}</dt>
            <dd>{formatDateTime(detail.answeredAt, locale, emptyValue)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-400">{t('supportInquiries.detail.author')}</dt>
            <dd>{detail.userName ?? emptyValue}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-400">{t('supportInquiries.detail.email')}</dt>
            <dd>{detail.userEmail ?? emptyValue}</dd>
          </div>
        </dl>
      </div>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-gray-900">{t('supportInquiries.detail.inquiryContent')}</h4>
        <div className="rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-800 whitespace-pre-wrap">
          {detail.content}
        </div>
        {detail.attachments.length > 0 && <AttachmentList attachments={detail.attachments} />}
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-gray-900">{t('supportInquiries.detail.answer')}</h4>
        {detail.status === 'pending' ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            {t('supportInquiries.detail.pendingAnswer')}
          </div>
        ) : latestReply ? (
          <div className="space-y-3 rounded-lg border border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{latestReply.authorName}</p>
                <p className="text-xs text-gray-400">
                  {formatDateTime(latestReply.createdAt, locale, emptyValue)}
                </p>
              </div>
            </div>
            <div className="whitespace-pre-wrap text-sm text-gray-800">{latestReply.content}</div>
            {latestReply.attachments.length > 0 && (
              <AttachmentList attachments={latestReply.attachments} />
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            {t('supportInquiries.detail.answerUnavailable')}
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="h-10 rounded-md border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {t('supportInquiries.actions.close')}
        </button>
      </div>
    </div>
  )
}

function AttachmentList({
  attachments,
}: {
  attachments: Array<{
    attachmentId: number
    fileName: string
    fileSize: number
    downloadUrl: string | null
  }>
}) {
  const { t } = useTranslation('admin')

  return (
    <ul className="space-y-2">
      {attachments.map((attachment) => (
        <li
          key={attachment.attachmentId}
          className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
        >
          <div className="min-w-0">
            <p className="truncate text-sm text-gray-900">{attachment.fileName}</p>
            <p className="text-xs text-gray-400">{formatFileSize(attachment.fileSize)}</p>
          </div>
          {attachment.downloadUrl ? (
            <a
              href={attachment.downloadUrl}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center gap-1 text-sm font-medium ${attachmentLinkClass(attachment.downloadUrl)}`}
            >
              <Download className="h-4 w-4" />
              {t('supportInquiries.actions.download')}
            </a>
          ) : (
            <span
              className={`inline-flex items-center gap-1 text-sm font-medium ${attachmentLinkClass(attachment.downloadUrl)}`}
            >
              <Download className="h-4 w-4" />
              {t('supportInquiries.actions.downloadUnavailable')}
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}
